import React from 'react';
import { message } from '../../utils/antdMessage';
import { apiFetch } from '../../utils/api';
import { hasPermission } from '../../utils/permissionControl';
import { formatPickPointListDetail } from '../../utils/pickPointCapture';
import IconPicker from '../../modules/iconLibrary/components/IconPicker';

/**
 * 绑定令牌 + 手机选点 + App 指令面板
 */
export default function BindAndPickPanel({
  appCommandForm,
  setAppCommandForm,
  sendAppCommand,
  appCommandLoading,
  toggleAppCommandRecipient,
  trackerTokens,
  trackerForm,
  setTrackerForm,
  createTrackerToken,
  assignStreamKey,
  trackerLoading,
  handleTrackerTtlDaysChange,
  copyTrackerLink,
  updateTrackerMode,
  revokeTrackerToken,
  pickPointForm,
  setPickPointForm,
  createPickPointToken,
  pickPointLoading,
  pickPointTokens,
  copyPickPointLink,
  revokePickPointToken,
  confirmPickPointToken,
  deletePickPointToken,
  fetchPickPointTokens
}) {
  return (
    <>
      <section
        style={{
          width: '100%',
          marginBottom: 14,
          padding: '12px 14px',
          borderRadius: 8,
          background: '#eef2ff',
          border: '1px solid #a5b4fc',
          boxSizing: 'border-box'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
          <h3
            style={{ margin: 0, fontSize: 15, color: '#3730a3' }}
            title="普通：首页横幅；重要：醒目提醒+铃声。App 需在运行/上报中拉取。"
          >
            下达指令到 App
          </h3>
          <button
            type="button"
            onClick={sendAppCommand}
            disabled={appCommandLoading}
            style={{
              padding: '7px 16px',
              minHeight: 34,
              border: 0,
              borderRadius: 5,
              color: '#fff',
              background: appCommandForm.important ? '#c2410c' : '#4338ca',
              cursor: appCommandLoading ? 'not-allowed' : 'pointer',
              fontWeight: 600,
              fontSize: 13,
              whiteSpace: 'nowrap'
            }}
          >
            {appCommandLoading ? '下达中…' : '下达指令'}
          </button>
        </div>
        <textarea
          value={appCommandForm.text}
          onChange={(event) => setAppCommandForm((form) => ({ ...form, text: event.target.value }))}
          placeholder="指令内容…"
          rows={3}
          style={{
            width: '100%',
            minHeight: 72,
            boxSizing: 'border-box',
            padding: '8px 10px',
            borderRadius: 5,
            border: '1px solid #a5b4fc',
            resize: 'vertical',
            fontFamily: 'inherit',
            fontSize: 13
          }}
        />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginTop: 8 }}>
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 12,
              color: appCommandForm.important ? '#b45309' : '#475569',
              fontWeight: appCommandForm.important ? 600 : 400,
              cursor: 'pointer'
            }}
            title="勾选后：醒目颜色 + 系统通知铃声"
          >
            <input
              type="checkbox"
              checked={!!appCommandForm.important}
              onChange={(event) => setAppCommandForm((form) => ({ ...form, important: event.target.checked }))}
            />
            重要
          </label>
          <label
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#475569', cursor: 'pointer' }}
            title="发给全部有效绑定 App；取消后可多选接收人"
          >
            <input
              type="checkbox"
              checked={!!appCommandForm.to_all}
              onChange={(event) =>
                setAppCommandForm((form) => ({
                  ...form,
                  to_all: event.target.checked,
                  selectedTokens: event.target.checked ? [] : form.selectedTokens
                }))
              }
            />
            全部 App
          </label>
        </div>
        {!appCommandForm.to_all && (
          <div
            style={{
              marginTop: 8,
              maxHeight: 160,
              minHeight: 56,
              overflowY: 'auto',
              fontSize: 12,
              background: '#fff',
              borderRadius: 5,
              padding: '6px 10px',
              border: '1px solid #c7d2fe'
            }}
          >
            {trackerTokens.filter((token) => token.status === 'active').length === 0 ? (
              <span style={{ color: '#94a3b8' }}>暂无有效 App</span>
            ) : (
              trackerTokens
                .filter((token) => token.status === 'active')
                .map((token) => (
                  <label key={token.token} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 0', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={(appCommandForm.selectedTokens || []).includes(token.token)}
                      onChange={() => toggleAppCommandRecipient(token.token)}
                    />
                    <span>
                      <strong>{token.name}</strong> · {token.group_name}
                    </span>
                  </label>
                ))
            )}
          </div>
        )}
      </section>
      <section
        style={{
          width: '100%',
          marginBottom: 14,
          padding: '12px 14px',
          borderRadius: 8,
          background: '#e5f7f5',
          border: '1px solid #87d5ce',
          boxSizing: 'border-box'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
          <h3 style={{ margin: 0, fontSize: 15, color: '#087f7b' }} title="生成绑定链接发给队员，在 App 中打开即可绑定">
            App 绑定令牌
          </h3>
          <button
            type="button"
            onClick={createTrackerToken}
            disabled={trackerLoading}
            style={{
              padding: '7px 16px',
              minHeight: 34,
              minWidth: 88,
              border: 0,
              borderRadius: 5,
              color: '#fff',
              background: '#087f7b',
              cursor: trackerLoading ? 'not-allowed' : 'pointer',
              fontSize: 13,
              fontWeight: 600,
              whiteSpace: 'nowrap'
            }}
          >
            {trackerLoading ? '生成中…' : '生成链接'}
          </button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.2fr 1fr auto', gap: 6, alignItems: 'center' }}>
          {[
            ['name', '姓名*'],
            ['group_name', '小组*'],
            ['org_name', '组织']
          ].map(([field, placeholder]) => (
            <input
              key={field}
              value={trackerForm[field]}
              placeholder={placeholder}
              onChange={(event) => setTrackerForm((form) => ({ ...form, [field]: event.target.value }))}
              style={{ padding: '6px 8px', border: '1px solid #9dcfc9', borderRadius: 4, fontSize: 13, minWidth: 0 }}
            />
          ))}
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }} title="有效天数；填 0 = 长期有效">
            <input
              type="number"
              min="0"
              max="365"
              value={trackerForm.ttl_days}
              onChange={handleTrackerTtlDaysChange}
              placeholder="30"
              style={{ width: 64, padding: '6px 8px', border: '1px solid #9dcfc9', borderRadius: 4, fontSize: 13 }}
            />
            <span style={{ fontSize: 12, color: '#087f7b', whiteSpace: 'nowrap', fontWeight: 600 }}>天</span>
          </label>
        </div>
        <input
          value={trackerForm.note}
          placeholder="备注"
          onChange={(event) => setTrackerForm((form) => ({ ...form, note: event.target.value }))}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            marginTop: 6,
            padding: '6px 8px',
            border: '1px solid #9dcfc9',
            borderRadius: 4,
            fontSize: 13
          }}
        />
        <input
          value={trackerForm.custom_token || ''}
          placeholder="自定义令牌（可选，4–64 位；亦作直播码；空则自动生成）"
          onChange={(event) => setTrackerForm((form) => ({ ...form, custom_token: event.target.value.trim() }))}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            marginTop: 6,
            padding: '6px 8px',
            border: '1px solid #9dcfc9',
            borderRadius: 4,
            fontSize: 13
          }}
        />
        <div style={{ marginTop: 10, maxHeight: 260, minHeight: 88, overflowY: 'auto', fontSize: 13 }}>
          {trackerTokens.filter((token) => token.status === 'active').length === 0 ? (
            <span style={{ color: '#61706f' }}>暂无令牌</span>
          ) : (
            trackerTokens
              .filter((token) => token.status === 'active')
              .map((token) => (
                <div
                  key={token.token}
                  style={{
                    display: 'flex',
                    flexWrap: 'nowrap',
                    alignItems: 'center',
                    gap: 6,
                    minHeight: 40,
                    padding: '8px 0',
                    borderTop: '1px solid #bee5e1'
                  }}
                >
                  <strong style={{ flexShrink: 0 }}>{token.name}</strong>
                  <span
                    title="推流码（只读，一令牌通吃 t≡stream_key）"
                    style={{
                      color: '#0f766e',
                      background: '#ecfdf5',
                      borderRadius: 4,
                      padding: '2px 6px',
                      fontSize: 11,
                      fontFamily: 'monospace',
                      flexShrink: 0,
                      maxWidth: 120,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {token.stream_key || token.token}
                  </span>
                  <button
                    type="button"
                    title="复制推流码"
                    onClick={async () => {
                      const sk = String(token.stream_key || token.token || '').trim();
                      if (!sk) return;
                      try {
                        await navigator.clipboard.writeText(sk);
                        window.alert('已复制推流码');
                      } catch (_) {
                        window.alert('复制失败');
                      }
                    }}
                    style={{ fontSize: 11, padding: '2px 6px', borderRadius: 4, cursor: 'pointer', flexShrink: 0 }}
                  >
                    复制码
                  </button>
                  <button
                    type="button"
                    title="按队员姓名生成短推流码（队伍前缀+姓名首字母+数字）"
                    onClick={async () => {
                      if (!assignStreamKey) {
                        window.alert('当前页未接入获取推流码');
                        return;
                      }
                      try {
                        await assignStreamKey({
                          deviceUniqueId: token.device_unique_id,
                          name: token.name,
                          force: false
                        });
                      } catch (err) {
                        window.alert(err.message || '获取推流码失败');
                      }
                    }}
                    style={{ fontSize: 11, padding: '2px 6px', borderRadius: 4, cursor: 'pointer', flexShrink: 0 }}
                  >
                    {token.stream_key || token.token ? '重新获取' : '获取'}
                  </button>
                  <span style={{ color: '#61706f', flexShrink: 0 }}>{token.group_name}</span>
                  <span style={{ color: '#61706f', flexShrink: 0 }}>{token.track_mode === 'boosted' ? '加强' : '省电'}</span>
                  {token.permanent && (
                    <span
                      title="长期有效"
                      style={{
                        color: '#1d4ed8',
                        background: '#dbeafe',
                        borderRadius: 999,
                        padding: '2px 8px',
                        fontSize: 11,
                        fontWeight: 700,
                        flexShrink: 0
                      }}
                    >
                      长期
                    </span>
                  )}
                  {token.bound || token.first_used_at ? (
                    <span
                      title={token.first_used_at ? `首次绑定：${new Date(token.first_used_at).toLocaleString()}` : '已绑定'}
                      style={{
                        color: '#065f5b',
                        background: '#b7efe9',
                        borderRadius: 999,
                        padding: '2px 8px',
                        fontSize: 11,
                        fontWeight: 700,
                        flexShrink: 0
                      }}
                    >
                      已绑
                    </span>
                  ) : (
                    <span
                      style={{
                        color: '#92400e',
                        background: '#fef3c7',
                        borderRadius: 999,
                        padding: '2px 8px',
                        fontSize: 11,
                        fontWeight: 600,
                        flexShrink: 0
                      }}
                    >
                      待绑
                    </span>
                  )}
                  <span style={{ marginLeft: 'auto', display: 'inline-flex', flexWrap: 'nowrap', gap: 6, flexShrink: 0 }}>
                    <button
                      type="button"
                      onClick={() => copyTrackerLink(token.token)}
                      style={{ fontSize: 12, padding: '5px 10px', minWidth: 44, minHeight: 28, borderRadius: 4, cursor: 'pointer' }}
                    >
                      复制令牌
                    </button>
                    <button
                      type="button"
                      onClick={() => updateTrackerMode(token.token, 'boosted')}
                      style={{ fontSize: 12, padding: '5px 10px', minWidth: 44, minHeight: 28, borderRadius: 4, cursor: 'pointer' }}
                    >
                      加强
                    </button>
                    <button
                      type="button"
                      onClick={() => updateTrackerMode(token.token, 'power_save')}
                      style={{ fontSize: 12, padding: '5px 10px', minWidth: 44, minHeight: 28, borderRadius: 4, cursor: 'pointer' }}
                    >
                      省电
                    </button>
                    <button
                      type="button"
                      onClick={() => revokeTrackerToken(token.token)}
                      style={{
                        fontSize: 12,
                        padding: '5px 10px',
                        minWidth: 44,
                        minHeight: 28,
                        borderRadius: 4,
                        cursor: 'pointer',
                        color: '#b42318'
                      }}
                    >
                      撤销
                    </button>
                  </span>
                </div>
              ))
          )}
        </div>
      </section>
      <section
        style={{
          width: '100%',
          marginBottom: 14,
          padding: '12px 14px',
          borderRadius: 8,
          background: '#fff4e6',
          border: '1px solid #fdba74',
          boxSizing: 'border-box'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
          <h3 style={{ margin: 0, fontSize: 15, color: '#c2410c' }} title="一次性选点链接。手机打开后默认用当前位置，精度小于 15 米后每 5 秒取样三次，指挥地图实时显示">
            手机选点
          </h3>
          <button
            type="button"
            onClick={createPickPointToken}
            disabled={pickPointLoading}
            style={{
              padding: '7px 16px',
              minHeight: 34,
              minWidth: 88,
              border: 0,
              borderRadius: 5,
              color: '#fff',
              background: '#ea580c',
              cursor: pickPointLoading ? 'not-allowed' : 'pointer',
              fontSize: 13,
              fontWeight: 600,
              whiteSpace: 'nowrap'
            }}
          >
            {pickPointLoading ? '生成中…' : '生成链接'}
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 6, alignItems: 'center' }}>
            <input
              value={pickPointForm.note}
              placeholder="备注（可选）"
              onChange={(event) => setPickPointForm((form) => ({ ...form, note: event.target.value }))}
              style={{ padding: '6px 8px', border: '1px solid #fdba74', borderRadius: 4, fontSize: 13 }}
            />
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }} title="链接有效分钟数">
              <input
                type="number"
                min="5"
                max="120"
                value={pickPointForm.ttl_minutes}
                onChange={(event) => setPickPointForm((form) => ({ ...form, ttl_minutes: event.target.value }))}
                placeholder="30"
                style={{ width: 64, padding: '6px 8px', border: '1px solid #fdba74', borderRadius: 4, fontSize: 13 }}
              />
              <span style={{ fontSize: 12, color: '#c2410c', whiteSpace: 'nowrap', fontWeight: 600 }}>分钟</span>
            </label>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, color: '#7c5c3e', marginBottom: 4 }}>地图图标</label>
            <IconPicker
              value={pickPointForm.icon_id}
              onChange={(value) => setPickPointForm((form) => ({ ...form, icon_id: value || null }))}
              source="uploaded"
              valueMode="id"
              allowClear
              placeholder="选择图标（未选则使用默认）"
              showPreview={false}
              size="small"
              style={{ width: '100%' }}
            />
          </div>
        </div>
        <div style={{ marginTop: 10, maxHeight: 220, minHeight: 72, overflowY: 'auto', fontSize: 13 }}>
          {pickPointTokens.length === 0 ? (
            <span style={{ color: '#7c5c3e' }}>暂无选点链接</span>
          ) : (
            pickPointTokens.map((item) => (
              <div
                key={item.token}
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 6,
                  minHeight: 40,
                  padding: '8px 0',
                  borderTop: '1px solid #fed7aa'
                }}
              >
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, flex: '1 1 auto', minWidth: 0 }}>
                  <strong style={{ flexShrink: 0 }}>{item.note || '未备注'}</strong>
                  {item.created_by_name && (
                    <span
                      style={{
                        color: '#8b6914',
                        background: '#fef3c7',
                        borderRadius: 999,
                        padding: '2px 8px',
                        fontSize: 11,
                        fontWeight: 600,
                        flexShrink: 0
                      }}
                      title="创建人"
                    >
                      {item.created_by_name}
                    </span>
                  )}
                  <span style={{ color: '#7c5c3e', flexShrink: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {formatPickPointListDetail(item) || item.status}
                  </span>
                </div>
                <span style={{ marginLeft: 'auto', display: 'inline-flex', flexWrap: 'nowrap', gap: 6, flexShrink: 0 }}>
                  <button
                    type="button"
                    onClick={() => copyPickPointLink(item)}
                    style={{ fontSize: 12, padding: '5px 10px', minWidth: 44, minHeight: 28, borderRadius: 4, cursor: 'pointer' }}
                  >
                    复制短链
                  </button>
                  {item.status === 'used' && !item.confirmed_at && (
                    <button
                      type="button"
                      onClick={() => confirmPickPointToken(item.token)}
                      style={{
                        fontSize: 12,
                        padding: '5px 10px',
                        minWidth: 44,
                        minHeight: 28,
                        borderRadius: 4,
                        cursor: 'pointer',
                        color: '#fff',
                        background: '#16a34a',
                        border: '1px solid #16a34a',
                        fontWeight: 600
                      }}
                    >
                      确认
                    </button>
                  )}
                  {item.confirmed_at && (
                    <span
                      title={`确认时间: ${new Date(item.confirmed_at).toLocaleString()}\n确认人: ${item.confirmed_by_name || '未知'}`}
                      style={{
                        color: '#16a34a',
                        background: '#dcfce7',
                        borderRadius: 999,
                        padding: '2px 10px',
                        fontSize: 11,
                        fontWeight: 700,
                        flexShrink: 0,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 2,
                        alignSelf: 'center'
                      }}
                    >
                      ✓ 已确认
                    </span>
                  )}
                  {item.status === 'active' && (
                    <button
                      type="button"
                      onClick={() => revokePickPointToken(item.token)}
                      style={{
                        fontSize: 12,
                        padding: '5px 10px',
                        minWidth: 44,
                        minHeight: 28,
                        borderRadius: 4,
                        cursor: 'pointer',
                        color: '#b42318'
                      }}
                    >
                      撤销
                    </button>
                  )}
                  {hasPermission('手机选点删除') && (
                    <button
                      type="button"
                      onClick={async () => {
                        if (!window.confirm('确定删除该选点记录？指挥地图上的标记也会消失。')) return;
                        await deletePickPointToken(item.id);
                      }}
                      style={{
                        fontSize: 12,
                        padding: '5px 10px',
                        minWidth: 44,
                        minHeight: 28,
                        borderRadius: 4,
                        cursor: 'pointer',
                        color: '#b42318'
                      }}
                    >
                      删除
                    </button>
                  )}
                </span>
              </div>
            ))
          )}
        </div>
      </section>
    </>
  );
}
